import { Box, MenuItem, Select, Typography } from '@mui/material';
import { Info } from '@phosphor-icons/react';
import React, { useState } from 'react';
import { useAuthStore } from '../../store/storeAuth';
import { useStoreMicrosoftData } from '../../store/storeMicrosoftData';
import Label from '../UI/Label/Label';
import ConnectToMicrosoft from './ConnectToMicrosoft';
import PullMicrosoftExcelData from './PullMicrosoftExcelData';
import PullMicrosoftPowerPointData from './PullMicrosoftPowerPointData';
import PullMicrosoftWordData from './PullMicrosoftWordData';

export enum MicrosoftService {
  Excel = 'Excel',
  Word = 'Word',
  PowerPoint = 'PowerPoint',
  RawFiles = 'Raw Files',
}

export const MicrosoftPullServices = [
  MicrosoftService.Excel,
  MicrosoftService.Word,
  MicrosoftService.PowerPoint,
] as const;

interface PullMicrosoftDataProps {
  formData: any;
  onChange: (e: { target: { name: string; value: any } }) => void;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  nodeId: string;
  nodeLabel: string;
}

const PullMicrosoftData: React.FC<PullMicrosoftDataProps> = ({
  formData,
  onChange,
  onChangeSelect,
  data,
  nodeId,
  nodeLabel,
}) => {
  const { isMicrosoftConnected } = useStoreMicrosoftData();
  const { user } = useAuthStore(state => state);

  const [selectedMicrosoftService, setSelectedMicrosoftService] = useState<MicrosoftService>(
    data.selectedMicrosoftService ?? MicrosoftService.Excel,
  );

  const onServiceChange = (e: { target: { value: unknown } }) => {
    const value = e.target.value as MicrosoftService;
    setSelectedMicrosoftService(value);
    onChangeSelect({
      target: { name: 'selectedMicrosoftService', value },
    });
  };

  const selectSx = {
    width: '60%',
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  if (!isMicrosoftConnected) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Label isAdvanced labelName="Database" />
        <Box
          sx={{
            p: '24px 20px',
            mb: '24px',
            borderRadius: '4px',
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='%2398A2B3FF' stroke-width='2' stroke-dasharray='6%2c 14' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
            display: 'flex',
            width: '386px',
            height: '146px',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Info color="#98A2B3" size={32} />
          <Typography
            sx={{
              fontSize: '12px',
              color: '#98A2B3',
              textAlign: 'center',
            }}
          >
            <ConnectToMicrosoft /> to access your Microsoft files and use them in
            workflows.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Label
        labelName="Data Type"
        isAdvanced
        labelStyle={{
          fontSize: '14px',
          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
          fontWeight: 400,
        }}
      />

      <Select
        className="nodrag"
        name="selectedMicrosoftService"
        value={selectedMicrosoftService}
        onChange={onServiceChange}
        sx={selectSx}
        MenuProps={menuPropsSx}
      >
        {MicrosoftPullServices.map(service => (
          <MenuItem
            key={service}
            value={service}
            sx={{
              color: user?.theme === 'dark' ? '#D0D5DD' : 'black',
            }}
          >
            {service}
          </MenuItem>
        ))}
      </Select>

      <Box sx={{ margin: '10px 0' }} />

      {selectedMicrosoftService === MicrosoftService.Excel && (
        <PullMicrosoftExcelData
          formData={formData}
          onChange={onChange}
          onChangeSelect={onChangeSelect}
          data={data}
          nodeId={nodeId}
          nodeLabel={nodeLabel}
        />
      )}

      {selectedMicrosoftService === MicrosoftService.Word && (
        <PullMicrosoftWordData
          formData={formData}
          onChange={onChange}
          onChangeSelect={onChangeSelect}
          data={data}
          nodeId={nodeId}
          nodeLabel={nodeLabel}
        />
      )}

      {selectedMicrosoftService === MicrosoftService.PowerPoint && (
        <PullMicrosoftPowerPointData
          formData={formData}
          onChange={onChange}
          onChangeSelect={onChangeSelect}
          data={data}
          nodeId={nodeId}
          nodeLabel={nodeLabel}
        />
      )}
    </Box>
  );
};

export default PullMicrosoftData;
